<template>

    <b-card>

        <b-row>

            <!-- Wallet Info: Left col -->
            <b-col cols="21"
                   xl="6"
                   class="d-flex flex-column">

                <!-- Wallet Avatar & Action Buttons -->
                <div class="d-flex justify-content-start align-items-center">

                    <b-avatar :src="walletData.avatar"
                              :text="this.avatarText( walletData.status ? 'Activo' : 'Inactivo' )"
                              :variant="`light-primary`"
                              size="104px"
                              rounded/>

                    <div class="d-flex flex-column ml-1">

                        <div class="mb-1">
                            <h4 class="mb-0">
                              {{$t("labels.wallet")}} {{ `${walletData.status ?  $t("labels.activeStatus") : $t("labels.inactiveStatus")}` }}
                            </h4>

                            <div class="d-flex flex-row">
                                <span class="card-text" style="margin-right: 7px">{{$t("labels.created")}} </span>
                                <span class="card-text font-weight-bold">{{ formatCreatedAt(walletData.createdAt) }}</span>
                            </div>

                        </div>

                    </div>
                </div>

                <!-- Wallet Stats -->
                <div class="d-flex align-items-center mt-2">

                    <div class="d-flex align-items-center mr-2">

                        <b-avatar variant="light-primary"
                                  rounded
                                  size="70px">
                            <feather-icon
                                    icon="DollarSignIcon"
                                    size="30"/>
                        </b-avatar>

                        <div class="ml-1">
                            <h5 class="mb-0">
                                {{selectedCurrency.symbol}} {{getBalance}}
                            </h5>
                            <small>{{$t("labels.currencyBalance")}}</small>
                        </div>
                    </div>

                    <div class="d-flex align-items-center">
                        <b-avatar
                                variant="light-success"
                                rounded
                                size="70px">

                            <coin-avatar :abv="selectedCurrency.abv" variant="silver"></coin-avatar>
                        </b-avatar>

                        <div class="ml-1">

                            <v-select v-model="selectedCurrency"
                                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                      :options="activatedCurrencies"
                                      style="min-width: 150px"
                                      :clearable="false"
                                      label="name"/>

                            <small>{{$t("labels.currency")}}</small>

                        </div>

                    </div>

                </div>

            </b-col>

            <!-- Right Col: Table -->
            <b-col cols="12"
                   xl="6">

                <table class="mt-2 mt-xl-0 w-100">

                    <tr>
                        <th class="pb-50">
                            <feather-icon
                                    icon="ToggleRightIcon"
                                    class="mr-75"
                            />
                            <span class="font-weight-bold">{{$t("labels.activeCurrencies")}}</span>
                        </th>
                        <td class="pb-50">
                            <b-badge id="popover-active-currency" variant="primary">{{ activatedCurrencies.length }}</b-badge>
                            <b-popover target="popover-active-currency" triggers="hover" placement="top">
                                <template #title>{{$t("labels.activeCurrencies")}}</template>
                                <b-list-group v-for="(item,index) in activatedCurrencies" :key="index" flush>
                                    <b-list-group-item style="padding: 5px">{{item.name}}</b-list-group-item>
                                </b-list-group>
                            </b-popover>
                        </td>
                    </tr>

                    <tr>
                        <th class="pb-50">
                            <feather-icon
                                    icon="ToggleLeftIcon"
                                    class="mr-75"
                            />
                            <span class="font-weight-bold">{{$t("labels.inactiveCurrencies")}}</span>
                        </th>
                        <td class="pb-50">
                            <b-badge id="popover-disabled-currency" variant="primary">{{ disabledCurrencies.length }}</b-badge>
                            <b-popover target="popover-disabled-currency" triggers="hover" placement="top">
                                <template #title>{{$t("labels.inactiveCurrencies")}}</template>
                                <b-list-group v-for="(item,index) in disabledCurrencies" :key="index" flush >
                                    <b-list-group-item style="padding: 5px;border-radius: 3px !important;">{{item.name}}</b-list-group-item>
                                </b-list-group>
                            </b-popover>
                        </td>
                    </tr>

                    <tr>
                        <th class="pb-50">
                            <feather-icon
                                    icon="InfoIcon"
                                    class="mr-75"
                            />
                            <span class="font-weight-bold">{{$t("labels.defaultCurrency")}}</span>
                        </th>
                        <td class="pb-50">
                            {{ walletData.defaultCurrency.name }}
                        </td>
                    </tr>

                </table>
            </b-col>

        </b-row>

    </b-card>

</template>

<script>
  import { avatarText } from '@/@core/utils/filter'
  import { BAvatar, BBadge, BButton, BCard, BCol, BRow,BListGroupItem,BListGroup,BPopover } from 'bootstrap-vue'
  import moment from 'moment'
  import CoinAvatar from '../currencies/CoinAvatar'
  import vSelect from 'vue-select'

  export default {
    name: 'WalletViewWalletInfoCard',
    components: {
      BCard, BButton, BRow, BCol, BAvatar, BBadge, CoinAvatar, vSelect,BListGroupItem,BListGroup,BPopover
    },

    props: {
      walletData: {
        type: Object,
        required: true,
      },
    },

    setup() {
      return {
        avatarText,
      }
    },

    data() {
      return {
        selectedCurrency: {},
      }
    },

    computed: {

      getBalance() {
        const found = this.walletData.currencies.find(currency => currency.currency.name === this.selectedCurrency.name)
        if (found)
          return found.balance.toFixed(2)
        else
          return 0
      },

      disabledCurrencies(){
        return this.walletData.currencies.filter(currency => !currency.active).map(currency => {
          return Object.assign({}, currency.currency)
        })
      },

      activatedCurrencies(){
        return this.walletData.currencies.filter(currency => currency.active).map(currency => {
          return Object.assign({}, currency.currency)
        })
      }

    },

    methods: {

      formatCreatedAt: function(value) {
        return moment(String(value)).format('DD-MM-YYYY HH:MM')
      },

      currencies() {
        return this.walletData.currencies.map(currencies => {
          return Object.assign({}, currencies.currency)
        })
      },

    },

    created() {
      this.selectedCurrency = this.walletData.defaultCurrency
    },

  }
</script>

<style>
    .popover-body{
        padding: 8px !important;
    }
</style>
